/**
 * tokens.ts — GENERERT fra theme/theme.ts (avtrykk 2176e5bb6ac5).
 * Rediger aldri denne filen. Endre temaet i kilden og kjør
 * `node scripts/generate-tokens.ts`, som CI verifiserer med `--check`.
 *
 * Statusmappene for @innspel/web: navnet innsenderen ser per spor (DD-22),
 * SETNINGEN innsenderen får (DD-08), ikonnavnet fra Ionicons, og tokennavnene
 * for farge. Fargeverdiene selv står i theme/generated/tokens.css.
 *
 * Farge bærer aldri mening alene — hver status har ikon og tekstetikett (DD-08).
 * `{frist}` i `vurderes` byttes med svarfristen som DATO (DD-28).
 */

/** Innspillets statuser. Nøklene er Submissions.SubmissionStatus i contracts/. */
export const submissionStatus = {
  ny: {
    statusType: "reviewing",
    ikon: "ellipse-outline",
    etikett: { feil: "Mottatt", onske: "Mottatt" },
    lofte: { feil: "Vi har fått den. Du hører fra oss.", onske: "Vi har fått den. Du hører fra oss." },
    fg: "textSecondary",
    bg: "neutralBg",
    variant: "outline",
  },
  vurderes: {
    statusType: "reviewing",
    ikon: "hourglass-outline",
    etikett: { feil: "Vurderes", onske: "Vurderes" },
    lofte: { feil: "Svar innen {frist} — også hvis det er nei.", onske: "Svar innen {frist} — også hvis det er nei." },
    fg: "info",
    bg: "infoBg",
    variant: "outline",
  },
  planlagt: {
    statusType: "active",
    ikon: "calendar-outline",
    etikett: { feil: "Planlagt", onske: "Planlagt" },
    lofte: { feil: "Du får beskjed når den er rettet.", onske: "Du får beskjed når det er levert." },
    fg: "brand",
    bg: "brandBg",
    variant: "outline",
  },
  bygges: {
    statusType: "active",
    ikon: "construct-outline",
    etikett: { feil: "Retter nå", onske: "Bygger" },
    lofte: { feil: "Du får beskjed når den er rettet.", onske: "Du får beskjed når det er levert." },
    fg: "brand",
    bg: "brandBg",
    variant: "filled",
  },
  kjent_feil: {
    statusType: "active",
    ikon: "warning-outline",
    etikett: { feil: "Kjent feil", onske: "Kjent feil" },
    lofte: { feil: "Kjent feil. Du får beskjed når den er rettet.", onske: "Kjent feil. Du får beskjed når den er rettet." },
    fg: "warning",
    bg: "warningBg",
    variant: "outline",
  },
  levert: {
    statusType: "complete",
    ikon: "checkmark-circle-outline",
    etikett: { feil: "Rettet", onske: "Levert" },
    lofte: { feil: "Ferdig. Ser du feilen igjen, send et nytt innspill.", onske: "Ferdig. Takk for ønsket." },
    fg: "success",
    bg: "successBg",
    variant: "outline",
  },
  avvist: {
    statusType: "closed",
    ikon: "close-circle-outline",
    etikett: { feil: "Avvist", onske: "Avvist" },
    lofte: { feil: "Vi har svart på denne.", onske: "Vi har svart på denne." },
    fg: "danger",
    bg: "dangerBg",
    variant: "outline",
  },
  duplikat: {
    statusType: "closed",
    ikon: "git-merge-outline",
    etikett: { feil: "Slått sammen", onske: "Slått sammen" },
    lofte: { feil: "Slått sammen med et innspill som ligner.", onske: "Slått sammen med et innspill som ligner." },
    fg: "neutral",
    bg: "neutralBg",
    variant: "outline",
  },
} as const;

/** Kjent feil-statusene (M2.4). Nøklene er KnownIssues.KnownIssueStatus i contracts/; «fikset» heter «Rettet» (DD-22). */
export const knownIssueStatus = {
  aktiv: {
    ikon: "warning-outline",
    etikett: "Aktiv",
    fg: "warning",
    bg: "warningBg",
    variant: "outline",
  },
  fikset: {
    ikon: "checkmark-circle-outline",
    etikett: "Rettet",
    fg: "success",
    bg: "successBg",
    variant: "dashed",
  },
} as const;

/** Løftet for `vurderes` uten frist: svaret loves, tidspunktet ikke (DD-07, DD-28). */
export const lofteUtenFrist = "Du får svar — også hvis det er nei.";

/** Tokennavn i den aktive paletten — aldri en fargeverdi. */
export type Tokennavn = (typeof submissionStatus)[keyof typeof submissionStatus]['fg' | 'bg'] | (typeof knownIssueStatus)[keyof typeof knownIssueStatus]['fg' | 'bg'];
